import { useMemo } from 'react'
import type { WithOptional } from '../../types'
import type { FindingAggregateResponseStatistics, Scale } from '../../utils/api-client/api-schemas'
import { getScaleQualifier, getSeveritiesByRankDescending } from '../../utils/scale-utils'
import { DropdownElement, DropdownFilter, type DropdownFilterProps, Filter } from '../dropdown-filter'
import { type FilterStateStore, type PerCategoryStatistics } from './filter-state-store'

export type GroupVisibility = 'hidden' | 'disabled' | 'enabled'

export type FindingsTableDropdownFilterProps = WithOptional<
  FilterStateStore,
  'toggleHasFix' | 'toggleHasTriage' | 'toggleHasScaAnalysis' | 'toggleAnalyzed'
> &
  Pick<DropdownFilterProps, 'resetIcon' | 'resetLabel'> & {
    contentClassName?: string
    /**
     * The scan's severity scale, which governs both severity groups: Pixee's suggested severity is
     * always expressed in the scan's own scale (see `TriageResultMapper` on the platform), so the
     * two filters are congruent by construction and share this one scale.
     */
    scale?: Scale
    toolSeverityIcon?: React.ReactNode
    suggestedSeverityIcon?: React.ReactNode
    statistics?: FindingAggregateResponseStatistics
    categoryStatistics?: PerCategoryStatistics
    groups?: {
      analyzed?: GroupVisibility
      hasFix?: GroupVisibility
      hasTriage?: GroupVisibility
      hasScaAnalysis?: GroupVisibility
      classification?: GroupVisibility
      fixResult?: GroupVisibility
      scaClassification?: GroupVisibility
      fixConfidence?: GroupVisibility
      severityUpdate?: GroupVisibility
      severity?: GroupVisibility
      suggestedSeverity?: GroupVisibility
    }
  }

export const FindingsTableDropdownFilter = ({
  resetFilters,
  hasFix,
  toggleHasFix,
  hasTriage,
  toggleHasTriage,
  hasScaAnalysis,
  toggleHasScaAnalysis,
  filterToAnalyzed,
  toggleAnalyzed,
  classification,
  toggleClassification,
  toggleClassificationGroup,
  fixResult,
  toggleFixResult,
  toggleFixResultGroup,
  patchStatus,
  togglePatchStatus,
  scaClassification,
  toggleScaClassification,
  toggleScaClassificationGroup,
  toggleNoFixNecessary,
  toggleFixResultBlocked,
  fixConfidence,
  toggleFixConfidence,
  toggleFixConfidenceGroup,
  severityUpdate,
  toggleSeverityUpdate,
  toggleSeverityUpdateGroup,
  severity,
  toggleSeverityLabel,
  toggleSeverityGroup,
  setSeverityMinScore,
  setSeverityMaxScore,
  suggestedSeverity,
  toggleSuggestedSeverityLabel,
  toggleSuggestedSeverityGroup,
  setSuggestedSeverityMinScore,
  setSuggestedSeverityMaxScore,
  contentClassName,
  isInitialState,
  resetIcon,
  resetLabel,
  scale,
  toolSeverityIcon,
  statistics,
  categoryStatistics,
  suggestedSeverityIcon,
  groups = {
    analyzed: 'hidden',
    hasFix: 'hidden',
    hasTriage: 'hidden',
    hasScaAnalysis: 'hidden',
    classification: 'enabled',
    fixResult: 'hidden',
    scaClassification: 'hidden',
    fixConfidence: 'enabled',
    severityUpdate: 'enabled',
    severity: 'enabled',
    suggestedSeverity: 'hidden',
  },
}: FindingsTableDropdownFilterProps) => {
  const switchGroupFilterElements = useMemo(() => {
    function* getFilters() {
      if (toggleAnalyzed && groups.analyzed !== 'hidden') {
        yield {
          type: 'switch',
          id: 'analyzed',
          text: 'Has Analysis',
          value: filterToAnalyzed ?? false,
          onToggle: toggleAnalyzed,
          disabled: groups.analyzed === 'disabled',
        } satisfies Filter
      }

      if (toggleHasFix && groups.hasFix !== 'hidden') {
        yield {
          type: 'switch',
          id: 'hasFix',
          text: 'Available Fix',
          value: hasFix,
          onToggle: toggleHasFix,
          disabled: groups.hasFix === 'disabled',
        } satisfies Filter
      }

      if (toggleHasTriage && groups.hasTriage !== 'hidden') {
        yield {
          type: 'switch',
          id: 'hasTriage',
          text: 'Has Triage',
          value: hasTriage,
          onToggle: toggleHasTriage,
          disabled: groups.hasTriage === 'disabled',
        } satisfies Filter
      }

      if (toggleHasScaAnalysis && groups.hasScaAnalysis !== 'hidden') {
        yield {
          type: 'switch',
          id: 'hasScaAnalysis',
          text: 'Has Analysis',
          value: hasScaAnalysis,
          onToggle: toggleHasScaAnalysis,
          disabled: groups.hasScaAnalysis === 'disabled',
        } satisfies Filter
      }
    }

    const filters = [...getFilters()]

    // Only render the group if there are visible filters
    if (filters.length === 0) {
      return [] as DropdownElement[]
    }

    const hasAnyDisabled = filters.some(f => f.disabled)

    return [
      {
        type: 'noLabelGroup',
        id: 'switchGroup',
        filters,
        disabled: hasAnyDisabled,
      },
      {
        type: 'divider',
        id: 'first-divider',
      },
    ] satisfies DropdownElement[]
  }, [
    toggleAnalyzed,
    filterToAnalyzed,
    groups.analyzed,
    toggleHasFix,
    toggleHasTriage,
    toggleHasScaAnalysis,
    hasFix,
    hasTriage,
    hasScaAnalysis,
    groups.hasFix,
    groups.hasTriage,
    groups.hasScaAnalysis,
  ])

  const groupElements = [
    buildClassificationGroup({
      visibility: groups.classification,
      classification,
      toggleClassification,
      toggleClassificationGroup,
      statistics: categoryStatistics?.classification ?? statistics,
    }),
    buildFixResultGroup({
      visibility: groups.fixResult,
      fixResult,
      patchStatus,
      classification,
      toggleFixResult,
      togglePatchStatus,
      toggleFixResultBlocked,
      toggleNoFixNecessary,
      toggleFixResultGroup,
      statistics: categoryStatistics?.fixResult ?? statistics,
      noFixNecessaryStatistics: statistics,
    }),
    buildScaClassificationGroup({
      visibility: groups.scaClassification,
      scaClassification,
      toggleScaClassification,
      toggleScaClassificationGroup,
      statistics: categoryStatistics?.scaClassification ?? statistics,
    }),
    buildFixConfidenceGroup({
      visibility: groups.fixConfidence,
      fixConfidence,
      toggleFixConfidence,
      toggleFixConfidenceGroup,
      statistics: categoryStatistics?.fixConfidence ?? statistics,
    }),
    buildSuggestedSeverityGroup({
      visibility: groups.suggestedSeverity,
      scale,
      suggestedSeverity,
      toggleSuggestedSeverityLabel,
      toggleSuggestedSeverityGroup,
      setSuggestedSeverityMinScore,
      setSuggestedSeverityMaxScore,
      icon: suggestedSeverityIcon,
    }),
    buildSeverityGroup({
      visibility: groups.severity,
      suggestedSeverityVisibility: groups.suggestedSeverity,
      scale,
      severity,
      toggleSeverityLabel,
      toggleSeverityGroup,
      setSeverityMinScore,
      setSeverityMaxScore,
      icon: toolSeverityIcon,
    }),
    buildSeverityUpdateGroup({
      visibility: groups.severityUpdate,
      severityUpdate,
      toggleSeverityUpdate,
      toggleSeverityUpdateGroup,
    }),
  ].filter(element => element !== undefined)

  const disabled = areAllGroupsHidden(groups)

  return (
    <DropdownFilter
      contentClassName={contentClassName}
      label="Findings Table Filters Menu"
      onToggleAllFiltersOff={resetFilters}
      isAnyNonDefaultFilterActive={!isInitialState()}
      resetIcon={resetIcon}
      resetLabel={resetLabel}
      filterElements={[...switchGroupFilterElements, ...groupElements]}
      disabled={disabled}
    />
  )
}

const areAllGroupsHidden = (groups: Exclude<FindingsTableDropdownFilterProps['groups'], undefined>) => {
  return Object.values(groups).every(visibility => visibility === 'hidden')
}

// An unset visibility renders nothing, same as an explicit 'hidden'.
const isGroupRendered = (visibility: GroupVisibility | undefined): visibility is 'enabled' | 'disabled' =>
  visibility === 'enabled' || visibility === 'disabled'

const buildClassificationGroup = ({
  visibility,
  classification,
  toggleClassification,
  toggleClassificationGroup,
  statistics,
}: {
  visibility: GroupVisibility | undefined
  classification: FilterStateStore['classification']
  toggleClassification: FilterStateStore['toggleClassification']
  toggleClassificationGroup: FilterStateStore['toggleClassificationGroup']
  statistics: FindingAggregateResponseStatistics | undefined
}): DropdownElement | undefined => {
  if (!isGroupRendered(visibility)) {
    return undefined
  }

  const isDisabled = visibility === 'disabled'
  const triageOutcome = statistics?.triage.outcome

  return {
    type: 'group',
    id: 'classification',
    text: 'Classification',
    filters: [
      {
        type: 'checkbox',
        id: 'true_positive',
        text: 'True positive',
        value: classification.true_positive ?? false,
        onToggle: () => toggleClassification('true_positive'),
        disabled: isDisabled,
        count: triageOutcome?.true_positive,
      },
      {
        type: 'checkbox',
        id: 'suspicious',
        text: 'Likely true positive',
        value: classification.suspicious ?? false,
        onToggle: () => toggleClassification('suspicious'),
        disabled: isDisabled,
        count: triageOutcome?.likely_true_positive,
      },
      {
        type: 'checkbox',
        id: 'inconclusive',
        text: 'Inconclusive',
        value: classification.inconclusive ?? false,
        onToggle: () => toggleClassification('inconclusive'),
        disabled: isDisabled,
        count: triageOutcome?.inconclusive,
      },
      {
        type: 'checkbox',
        id: 'false_positive',
        text: 'False positive',
        value: classification.false_positive ?? false,
        onToggle: () => toggleClassification('false_positive'),
        disabled: isDisabled,
        count: triageOutcome?.false_positive,
      },
      {
        type: 'checkbox',
        id: 'wont_fix',
        text: "Won't fix",
        value: classification.wont_fix ?? false,
        onToggle: () => toggleClassification('wont_fix'),
        disabled: isDisabled,
        count: triageOutcome?.wont_fix,
      },
      {
        type: 'checkbox',
        id: 'blocked',
        text: 'Blocked',
        value: classification.blocked ?? false,
        onToggle: () => toggleClassification('blocked'),
        disabled: isDisabled,
        count: triageOutcome?.blocked,
      },
      {
        type: 'checkbox',
        id: 'excluded',
        text: 'Excluded',
        value: classification.excluded ?? false,
        onToggle: () => toggleClassification('excluded'),
        disabled: isDisabled,
        count: triageOutcome?.excluded,
      },
      {
        type: 'checkbox',
        id: 'error',
        text: 'Error',
        value: classification.error ?? false,
        onToggle: () => toggleClassification('error'),
        disabled: isDisabled,
        count: statistics?.triage.status.failed,
      },
    ],
    onToggleGroup: toggleClassificationGroup,
    disabled: isDisabled,
  }
}

const buildFixResultGroup = ({
  visibility,
  fixResult,
  patchStatus,
  classification,
  toggleFixResult,
  togglePatchStatus,
  toggleFixResultBlocked,
  toggleNoFixNecessary,
  toggleFixResultGroup,
  statistics,
  noFixNecessaryStatistics,
}: {
  visibility: GroupVisibility | undefined
  fixResult: FilterStateStore['fixResult']
  patchStatus: FilterStateStore['patchStatus']
  classification: FilterStateStore['classification']
  toggleFixResult: FilterStateStore['toggleFixResult']
  togglePatchStatus: FilterStateStore['togglePatchStatus']
  toggleFixResultBlocked: FilterStateStore['toggleFixResultBlocked']
  toggleNoFixNecessary: FilterStateStore['toggleNoFixNecessary']
  toggleFixResultGroup: FilterStateStore['toggleFixResultGroup']
  statistics: FindingAggregateResponseStatistics | undefined
  /**
   * The "No fix necessary" count reads the overall statistics, not the per-category `fixResult`
   * statistics its sibling counts use. The two sources are threaded in separately because the
   * difference is observable in the rendered count.
   */
  noFixNecessaryStatistics: FindingAggregateResponseStatistics | undefined
}): DropdownElement | undefined => {
  if (!isGroupRendered(visibility)) {
    return undefined
  }

  const isDisabled = visibility === 'disabled'
  const fixOutcome = statistics?.fix.outcome
  const fixStatus = statistics?.fix.status

  return {
    type: 'group',
    id: 'fixResult',
    text: 'Fix Result',
    filters: [
      {
        type: 'checkbox',
        id: 'fixResult-completed',
        text: 'Available fix',
        value: fixResult.completed ?? false,
        onToggle: () => toggleFixResult('completed'),
        disabled: isDisabled,
        count: fixOutcome?.has_fix,
      },
      {
        type: 'checkbox',
        id: 'patchStatus-issued',
        text: 'Fix issued',
        value: patchStatus.issued ?? false,
        onToggle: () => togglePatchStatus('issued'),
        disabled: isDisabled,
        count: undefined,
      },
      {
        type: 'checkbox',
        id: 'patchStatus-merged',
        text: 'Fix merged',
        value: patchStatus.merged ?? false,
        onToggle: () => togglePatchStatus('merged'),
        disabled: isDisabled,
        count: undefined,
      },
      {
        type: 'checkbox',
        id: 'no-fix-necessary',
        text: 'No fix necessary',
        value: (classification.false_positive ?? false) && (classification.wont_fix ?? false),
        onToggle: () => toggleNoFixNecessary(),
        disabled: isDisabled,
        count: sumCounts(
          noFixNecessaryStatistics?.triage.outcome.false_positive,
          noFixNecessaryStatistics?.triage.outcome.wont_fix
        ),
      },
      {
        type: 'checkbox',
        id: 'fixResult-no-recommendations-available',
        text: 'No recommendations',
        value: fixResult['no-recommendations-available'] ?? false,
        onToggle: () => toggleFixResult('no-recommendations-available'),
        disabled: isDisabled,
        count: fixStatus?.no_recommendations,
      },
      {
        type: 'checkbox',
        id: 'fixResult-blocked',
        text: 'Blocked',
        value: fixResult.blocked ?? false,
        onToggle: () => toggleFixResultBlocked(),
        disabled: isDisabled,
        count: fixOutcome?.blocked,
      },
      {
        type: 'checkbox',
        id: 'fixResult-excluded',
        text: 'Excluded',
        value: fixResult.excluded ?? false,
        onToggle: () => toggleFixResult('excluded'),
        disabled: isDisabled,
        count: fixOutcome?.excluded,
      },
      {
        type: 'checkbox',
        id: 'fixResult-failed',
        text: 'Error',
        value: fixResult.failed ?? false,
        onToggle: () => toggleFixResult('failed'),
        disabled: isDisabled,
        count: fixStatus?.failed,
      },
    ],
    onToggleGroup: toggleFixResultGroup,
    disabled: isDisabled,
  }
}

const buildScaClassificationGroup = ({
  visibility,
  scaClassification,
  toggleScaClassification,
  toggleScaClassificationGroup,
  statistics,
}: {
  visibility: GroupVisibility | undefined
  scaClassification: FilterStateStore['scaClassification']
  toggleScaClassification: FilterStateStore['toggleScaClassification']
  toggleScaClassificationGroup: FilterStateStore['toggleScaClassificationGroup']
  statistics: FindingAggregateResponseStatistics | undefined
}): DropdownElement | undefined => {
  if (!isGroupRendered(visibility)) {
    return undefined
  }

  const isDisabled = visibility === 'disabled'
  const scaClassificationCounts = statistics?.sca.classification

  return {
    type: 'group',
    id: 'scaClassification',
    text: 'Classification',
    filters: [
      {
        type: 'checkbox',
        id: 'exploitable',
        text: 'Exploitable',
        value: scaClassification.exploitable ?? false,
        onToggle: () => toggleScaClassification('exploitable'),
        disabled: isDisabled,
        count: scaClassificationCounts?.exploitable,
      },
      {
        type: 'checkbox',
        id: 'not-exploitable',
        text: 'Not Exploitable',
        value: scaClassification['not-exploitable'] ?? false,
        onToggle: () => toggleScaClassification('not-exploitable'),
        disabled: isDisabled,
        count: scaClassificationCounts?.not_exploitable,
      },
      {
        type: 'checkbox',
        id: 'potentially-exploitable',
        text: 'Potentially exploitable',
        value: scaClassification['potentially-exploitable'] ?? false,
        onToggle: () => toggleScaClassification('potentially-exploitable'),
        disabled: isDisabled,
        count: scaClassificationCounts?.inconclusive,
      },
    ],
    onToggleGroup: toggleScaClassificationGroup,
    disabled: isDisabled,
  }
}

const buildFixConfidenceGroup = ({
  visibility,
  fixConfidence,
  toggleFixConfidence,
  toggleFixConfidenceGroup,
  statistics,
}: {
  visibility: GroupVisibility | undefined
  fixConfidence: FilterStateStore['fixConfidence']
  toggleFixConfidence: FilterStateStore['toggleFixConfidence']
  toggleFixConfidenceGroup: FilterStateStore['toggleFixConfidenceGroup']
  statistics: FindingAggregateResponseStatistics | undefined
}): DropdownElement | undefined => {
  if (!isGroupRendered(visibility)) {
    return undefined
  }

  const isDisabled = visibility === 'disabled'
  const fixConfidenceCounts = statistics?.fix.confidence

  return {
    type: 'group',
    id: 'fixConfidence',
    text: 'Fix Confidence',
    filters: [
      {
        type: 'checkbox',
        id: 'high',
        text: 'High confidence',
        value: fixConfidence.high ?? false,
        onToggle: () => toggleFixConfidence('high'),
        disabled: isDisabled,
        count: fixConfidenceCounts?.high,
      },
      {
        type: 'checkbox',
        id: 'medium',
        text: 'Medium confidence',
        value: fixConfidence.medium ?? false,
        onToggle: () => toggleFixConfidence('medium'),
        disabled: isDisabled,
        count: fixConfidenceCounts?.medium,
      },
      {
        type: 'checkbox',
        id: 'low',
        text: 'Low confidence',
        value: fixConfidence.low ?? false,
        onToggle: () => toggleFixConfidence('low'),
        disabled: isDisabled,
        count: fixConfidenceCounts?.low,
      },
      {
        type: 'checkbox',
        id: 'no-rating',
        text: 'No rating',
        value: fixConfidence['no-rating'] ?? false,
        onToggle: () => toggleFixConfidence('no-rating'),
        disabled: isDisabled,
        count: fixConfidenceCounts?.no_rating,
      },
    ],
    onToggleGroup: toggleFixConfidenceGroup,
    disabled: isDisabled,
  }
}

const buildSuggestedSeverityGroup = ({
  visibility,
  scale,
  suggestedSeverity,
  toggleSuggestedSeverityLabel,
  toggleSuggestedSeverityGroup,
  setSuggestedSeverityMinScore,
  setSuggestedSeverityMaxScore,
  icon,
}: {
  visibility: GroupVisibility | undefined
  scale: Scale | undefined
  suggestedSeverity: FilterStateStore['suggestedSeverity']
  toggleSuggestedSeverityLabel: FilterStateStore['toggleSuggestedSeverityLabel']
  toggleSuggestedSeverityGroup: FilterStateStore['toggleSuggestedSeverityGroup']
  setSuggestedSeverityMinScore: FilterStateStore['setSuggestedSeverityMinScore']
  setSuggestedSeverityMaxScore: FilterStateStore['setSuggestedSeverityMaxScore']
  icon: React.ReactNode
}): DropdownElement | undefined => {
  if (!isGroupRendered(visibility) || !scale) {
    return undefined
  }

  const isDisabled = visibility === 'disabled'
  const text = withScaleQualifier('Pixee Severity', scale)

  if (scale.type === 'score') {
    return buildScoreInputGroup({
      id: 'suggestedSeverity-score',
      text,
      icon,
      score: suggestedSeverity,
      scale,
      onMinScoreChange: setSuggestedSeverityMinScore,
      onMaxScoreChange: setSuggestedSeverityMaxScore,
      disabled: isDisabled,
    })
  }

  return buildSeverityLabelGroup({
    id: 'suggestedSeverity',
    checkboxIdPrefix: 'suggested-severity',
    text,
    icon,
    labels: suggestedSeverity.labels,
    scale,
    onToggleLabel: toggleSuggestedSeverityLabel,
    onToggleGroup: toggleSuggestedSeverityGroup,
    disabled: isDisabled,
  })
}

const buildSeverityGroup = ({
  visibility,
  suggestedSeverityVisibility,
  scale,
  severity,
  toggleSeverityLabel,
  toggleSeverityGroup,
  setSeverityMinScore,
  setSeverityMaxScore,
  icon,
}: {
  visibility: GroupVisibility | undefined
  suggestedSeverityVisibility: GroupVisibility | undefined
  scale: Scale | undefined
  severity: FilterStateStore['severity']
  toggleSeverityLabel: FilterStateStore['toggleSeverityLabel']
  toggleSeverityGroup: FilterStateStore['toggleSeverityGroup']
  setSeverityMinScore: FilterStateStore['setSeverityMinScore']
  setSeverityMaxScore: FilterStateStore['setSeverityMaxScore']
  icon: React.ReactNode
}): DropdownElement | undefined => {
  if (!isGroupRendered(visibility) || !scale) {
    return undefined
  }

  const isDisabled = visibility === 'disabled'
  // The tool scale only needs to be labelled "Tool Severity" when the Pixee severity group is
  // rendered alongside it; on its own it is simply "Severity".
  const text = withScaleQualifier(isGroupRendered(suggestedSeverityVisibility) ? 'Tool Severity' : 'Severity', scale)

  if (scale.type === 'score') {
    return buildScoreInputGroup({
      id: 'severity-score',
      text,
      icon,
      score: severity,
      scale,
      onMinScoreChange: setSeverityMinScore,
      onMaxScoreChange: setSeverityMaxScore,
      disabled: isDisabled,
    })
  }

  return buildSeverityLabelGroup({
    id: 'severity',
    checkboxIdPrefix: 'severity',
    text,
    icon,
    labels: severity.labels,
    scale,
    onToggleLabel: toggleSeverityLabel,
    onToggleGroup: toggleSeverityGroup,
    disabled: isDisabled,
  })
}

/**
 * Builds the checkbox list a ranked-set scale renders, ordered most severe first.
 *
 * Shared by both severity groups, for the same reason as `buildScoreInputGroup`.
 */
const buildSeverityLabelGroup = ({
  id,
  checkboxIdPrefix,
  text,
  icon,
  labels,
  scale,
  onToggleLabel,
  onToggleGroup,
  disabled,
}: {
  id: string
  checkboxIdPrefix: string
  text: string
  icon: React.ReactNode
  labels: Record<string, boolean>
  scale: Extract<Scale, { type: 'ranked-set' }>
  onToggleLabel: (label: string) => unknown
  onToggleGroup: (availableLabels: string[]) => unknown
  disabled: boolean
}): DropdownElement | undefined => {
  if (!scale.severities) {
    return undefined
  }

  const sortedSeverities = getSeveritiesByRankDescending(scale)

  return {
    type: 'group',
    id,
    text,
    icon,
    filters: sortedSeverities.map(sev => ({
      type: 'checkbox',
      id: `${checkboxIdPrefix}-${sev.label}`,
      text: sev.label,
      value: labels[sev.label] ?? false,
      onToggle: () => onToggleLabel(sev.label),
      disabled,
    })),
    onToggleGroup: () => onToggleGroup(sortedSeverities.map(sev => sev.label)),
    disabled,
  }
}

/**
 * Builds the From/To range a score scale renders instead of a checkbox list.
 *
 * Shared by both severity groups: they are the same control over different slices of the store, and
 * a second copy is a second place for the next change to it to be missed.
 */
const buildScoreInputGroup = ({
  id,
  text,
  icon,
  score,
  scale,
  onMinScoreChange,
  onMaxScoreChange,
  disabled,
}: {
  id: string
  text: string
  icon: React.ReactNode
  score: { minScore: number | null; maxScore: number | null }
  scale: Extract<Scale, { type: 'score' }>
  onMinScoreChange: (value: number | null) => unknown
  onMaxScoreChange: (value: number | null) => unknown
  disabled: boolean
}): DropdownElement => ({
  type: 'scoreInputGroup',
  id,
  text,
  icon,
  minScore: score.minScore,
  maxScore: score.maxScore,
  min: scale.min ?? 0,
  max: scale.max ?? 10,
  step: 0.1,
  onMinScoreChange,
  onMaxScoreChange,
  disabled,
})

/**
 * Appends the scale's units to a group label, so a From/To range states what it is measuring.
 * Ranked-set scales carry no qualifier and are returned unchanged.
 */
const withScaleQualifier = (label: string, scale: Scale | undefined): string => {
  const qualifier = getScaleQualifier(scale)
  return qualifier ? `${label} (${qualifier})` : label
}

const buildSeverityUpdateGroup = ({
  visibility,
  severityUpdate,
  toggleSeverityUpdate,
  toggleSeverityUpdateGroup,
}: {
  visibility: GroupVisibility | undefined
  severityUpdate: FilterStateStore['severityUpdate']
  toggleSeverityUpdate: FilterStateStore['toggleSeverityUpdate']
  toggleSeverityUpdateGroup: FilterStateStore['toggleSeverityUpdateGroup']
}): DropdownElement | undefined => {
  if (!isGroupRendered(visibility)) {
    return undefined
  }

  const isDisabled = visibility === 'disabled'

  return {
    type: 'group',
    id: 'severityUpdate',
    text: 'Severity Update',
    filters: [
      {
        type: 'checkbox',
        id: 'decreased',
        text: 'Severity decrease',
        value: severityUpdate.decreased ?? false,
        onToggle: () => toggleSeverityUpdate('decreased'),
        disabled: isDisabled,
      },
      {
        type: 'checkbox',
        id: 'increased',
        text: 'Severity increase',
        value: severityUpdate.increased ?? false,
        onToggle: () => toggleSeverityUpdate('increased'),
        disabled: isDisabled,
      },
      {
        type: 'checkbox',
        id: 'no_update',
        text: 'No update',
        value: severityUpdate.no_update ?? false,
        onToggle: () => toggleSeverityUpdate('no_update'),
        disabled: isDisabled,
      },
    ],
    onToggleGroup: toggleSeverityUpdateGroup,
    disabled: isDisabled,
  }
}

const sumCounts = (...values: (number | undefined)[]): number | undefined => {
  const defined = values.filter((v): v is number => v !== undefined)
  if (defined.length === 0) {
    return undefined
  }
  return defined.reduce((a, b) => a + b, 0)
}
